import React, { useState, useEffect } from 'react';
import { FileCheck, CreditCard, AlertCircle, Plus, X, Eye, Edit, CheckCircle, XCircle, Clock, Send, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import ICD10CodeSearch from './ICD10CodeSearch';
import { getAllICD10Codes } from '../data/icd10Codes';

// Common CPT Codes for Mental Health Services
const CPT_CODES = [
  { code: '90791', description: 'Psychiatric diagnostic evaluation (60 min)' },
  { code: '90792', description: 'Psychiatric diagnostic evaluation with medical services' },
  { code: '90832', description: 'Psychotherapy 30 minutes' },
  { code: '90834', description: 'Psychotherapy 45 minutes' },
  { code: '90837', description: 'Psychotherapy 60 minutes' },
  { code: '90839', description: 'Psychotherapy for crisis (first 60 min)' },
  { code: '90846', description: 'Family psychotherapy without client present' },
  { code: '90847', description: 'Family psychotherapy with client present' },
  { code: '90853', description: 'Group psychotherapy' },
  { code: '96127', description: 'Brief emotional/behavioral assessment' },
  { code: '99211', description: 'Office visit - established client (minimal)' },
  { code: '99212', description: 'Office visit - established client (straightforward)' },
  { code: '99213', description: 'Office visit - established client (low complexity)' },
  { code: '99214', description: 'Office visit - established client (moderate complexity)' },
];

const InsuranceClaimsManagement = ({ token, apiCall, clients = [], toast }) => {
  const [activeTab, setActiveTab] = useState('verifications');
  const [verifications, setVerifications] = useState([]);
  const [claims, setClaims] = useState([]);
  const [claimsStats, setClaimsStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Get all ICD-10 codes (500+)
  const ICD10_CODES = getAllICD10Codes();
  
  // Modals
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showClaimDetailModal, setShowClaimDetailModal] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  
  // Form data
  const [verificationForm, setVerificationForm] = useState({
    client_id: '',
    insurance_provider: '',
    policy_number: '',
    group_number: '',
    subscriber_name: '',
    subscriber_relationship: 'self',
    eligibility_status: 'active',
    effective_date: '',
    termination_date: '',
    copay_amount: '',
    deductible_amount: '',
    deductible_met: '0',
    out_of_pocket_max: '',
    out_of_pocket_met: '0',
    coverage_percentage: '80',
    verification_notes: ''
  });
  
  const [claimForm, setClaimForm] = useState({
    client_id: '',
    session_id: '',
    service_date: '',
    provider_name: '',
    provider_npi: '',
    cpt_codes: [],
    diagnosis_codes: [],
    service_description: '',
    billed_amount: '',
    insurance_provider: '',
    policy_number: '',
    claim_notes: ''
  });
  
  // Dropdown states
  const [showCptDropdown, setShowCptDropdown] = useState(false);
  const [showIcdDropdown, setShowIcdDropdown] = useState(false);

  useEffect(() => {
    if (activeTab === 'verifications') {
      loadVerifications();
    } else if (activeTab === 'claims') {
      loadClaims();
      loadClaimsStats();
    }
  }, [activeTab]);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowCptDropdown(false);
        setShowIcdDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadVerifications = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/insurance/verifications', 'GET', null, token);
      // console.log('📋 Verifications API response:', response);
      
      // Handle double-wrapped response from apiCall
      const verificationsData = response?.data?.data || response?.data || [];
      
      if (Array.isArray(verificationsData)) {
        setVerifications(verificationsData);
      } else {
        console.warn('⚠️ Verifications data is not an array:', verificationsData);
        setVerifications([]);
      }
    } catch (error) {
      console.error('Error loading verifications:', error);
      toast.error('Failed to load insurance verifications');
      setVerifications([]);
    }
    setLoading(false);
  };

  const loadClaims = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/insurance/claims', 'GET', null, token);
      // console.log('📋 Claims API response:', response);
      
      // Handle double-wrapped response from apiCall
      const claimsData = response?.data?.data || response?.data || [];
      
      if (Array.isArray(claimsData)) {
        setClaims(claimsData);
      } else {
        console.warn('⚠️ Claims data is not an array:', claimsData);
        setClaims([]);
      }
    } catch (error) {
      console.error('Error loading claims:', error);
      toast.error('Failed to load insurance claims');
      setClaims([]);
    }
    setLoading(false);
  };

  const loadClaimsStats = async () => {
    try {
      const response = await apiCall('/insurance/claims/stats/summary', 'GET', null, token);
      // console.log('📊 Claims stats API response:', response);
      
      // Handle double-wrapped response from apiCall
      const statsData = response?.data?.data || response?.data;
      
      if (statsData) {
        setClaimsStats(statsData);
      }
    } catch (error) {
      console.error('Error loading claims stats:', error);
    }
  };

  const handleCreateVerification = async () => {
    if (!verificationForm.client_id || !verificationForm.insurance_provider || !verificationForm.policy_number) {
      toast.error('Please fill in required fields (Client, Provider, Policy Number)');
      return;
    }

    try {
      const payload = {
        ...verificationForm,
        copay_amount: verificationForm.copay_amount ? parseFloat(verificationForm.copay_amount) : null,
        deductible_amount: verificationForm.deductible_amount ? parseFloat(verificationForm.deductible_amount) : null,
        deductible_met: verificationForm.deductible_met ? parseFloat(verificationForm.deductible_met) : 0,
        out_of_pocket_max: verificationForm.out_of_pocket_max ? parseFloat(verificationForm.out_of_pocket_max) : null,
        out_of_pocket_met: verificationForm.out_of_pocket_met ? parseFloat(verificationForm.out_of_pocket_met) : 0,
        coverage_percentage: verificationForm.coverage_percentage ? parseInt(verificationForm.coverage_percentage) : 80
      };

      const response = await apiCall('/insurance/verifications', 'POST', payload, token);
      if (response && response.id) {
        toast.success('✅ Insurance verification created successfully!');
        setShowVerificationModal(false);
        resetVerificationForm();
        loadVerifications();
      }
    } catch (error) {
      console.error('Error creating verification:', error);
      toast.error('Failed to create insurance verification');
    }
  };

  const handleCreateClaim = async () => {
    if (!claimForm.client_id || !claimForm.service_date || !claimForm.billed_amount) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const payload = {
        ...claimForm,
        billed_amount: parseFloat(claimForm.billed_amount),
        // cpt_codes and diagnosis_codes are already arrays
        cpt_codes: claimForm.cpt_codes,
        diagnosis_codes: claimForm.diagnosis_codes
      };

      const response = await apiCall('/insurance/claims', 'POST', payload, token);
      if (response && response.id) {
        toast.success('✅ Insurance claim created successfully!');
        setShowClaimModal(false);
        resetClaimForm();
        loadClaims();
        loadClaimsStats();
      }
    } catch (error) {
      console.error('Error creating claim:', error);
      toast.error('Failed to create insurance claim');
    }
  };

  const handleSubmitClaim = async (claimId) => {
    try {
      const response = await apiCall(`/insurance/claims/${claimId}/submit`, 'POST', {}, token);
      if (response.success) {
        toast.success('✅ Claim submitted successfully!');
        loadClaims();
        loadClaimsStats();
      }
    } catch (error) {
      console.error('Error submitting claim:', error);
      toast.error('Failed to submit claim');
    }
  };

  const handleUpdateClaimStatus = async (claimId, status) => {
    try {
      const response = await apiCall(`/insurance/claims/${claimId}`, 'PUT', { status }, token);
      if (response.success) {
        toast.success(`✅ Claim status updated to ${status}`);
        loadClaims();
        loadClaimsStats();
        if (showClaimDetailModal) {
          const updatedClaim = await apiCall(`/insurance/claims/${claimId}`, 'GET', null, token);
          if (updatedClaim.success) {
            setSelectedClaim(updatedClaim.data);
          }
        }
      }
    } catch (error) {
      console.error('Error updating claim:', error);
      toast.error('Failed to update claim status');
    }
  };

  const resetVerificationForm = () => {
    setVerificationForm({
      client_id: '',
      insurance_provider: '',
      policy_number: '',
      group_number: '',
      subscriber_name: '',
      subscriber_relationship: 'self',
      eligibility_status: 'active',
      effective_date: '',
      termination_date: '',
      copay_amount: '',
      deductible_amount: '',
      deductible_met: '0',
      out_of_pocket_max: '',
      out_of_pocket_met: '0',
      coverage_percentage: '80',
      verification_notes: ''
    });
  };

  const resetClaimForm = () => {
    setClaimForm({
      client_id: '',
      session_id: '',
      service_date: '',
      provider_name: '',
      provider_npi: '',
      cpt_codes: [],
      diagnosis_codes: [],
      service_description: '',
      billed_amount: '',
      insurance_provider: '',
      policy_number: '',
      claim_notes: ''
    });
    setShowCptDropdown(false);
    setShowIcdDropdown(false);
  };
  
  const toggleCptCode = (code) => {
    setClaimForm(prev => ({
      ...prev,
      cpt_codes: prev.cpt_codes.includes(code)
        ? prev.cpt_codes.filter(c => c !== code)
        : [...prev.cpt_codes, code]
    }));
  };
  
  const toggleDiagnosisCode = (code) => {
    setClaimForm(prev => ({
      ...prev,
      diagnosis_codes: prev.diagnosis_codes.includes(code)
        ? prev.diagnosis_codes.filter(c => c !== code)
        : [...prev.diagnosis_codes, code]
    }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      submitted: { color: 'bg-blue-100 text-blue-800', label: 'Submitted' },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      denied: { color: 'bg-red-100 text-red-800', label: 'Denied' },
      appealed: { color: 'bg-purple-100 text-purple-800', label: 'Appealed' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.first_name} ${client.last_name}` : 'Unknown Client';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Insurance Claims Management</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Manage insurance verifications, claims submissions, and denials
              </p>
            </div>
            <Badge className="bg-red-100 text-red-800">Admin Access Only</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="verifications">
                <FileCheck className="w-4 h-4 mr-2" />
                Verifications ({verifications.length})
              </TabsTrigger>
              <TabsTrigger value="claims">
                <CreditCard className="w-4 h-4 mr-2" />
                Claims ({claims.length})
              </TabsTrigger>
              <TabsTrigger value="denials">
                <AlertCircle className="w-4 h-4 mr-2" />
                Denials
              </TabsTrigger>
            </TabsList>

            {/* Insurance Verifications Tab */}
            <TabsContent value="verifications">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Insurance Verifications</h3>
                  <Button onClick={() => setShowVerificationModal(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Verify New Insurance
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading verifications...</div>
                ) : verifications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No insurance verifications yet. Click "Verify New Insurance" to get started.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {verifications.map((verification) => (
                      <Card key={verification.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold">{getClientName(verification.client_id)}</h4>
                                <Badge className={verification.eligibility_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                  {verification.eligibility_status}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                <div>
                                  <span className="text-gray-600">Provider:</span>
                                  <span className="ml-2 font-medium">{verification.insurance_provider}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Policy #:</span>
                                  <span className="ml-2 font-medium">{verification.policy_number}</span>
                                </div>
                                {verification.copay_amount && (
                                  <div>
                                    <span className="text-gray-600">Copay:</span>
                                    <span className="ml-2 font-medium">${verification.copay_amount}</span>
                                  </div>
                                )}
                                {verification.deductible_amount && (
                                  <div>
                                    <span className="text-gray-600">Deductible:</span>
                                    <span className="ml-2 font-medium">${verification.deductible_met || 0} / ${verification.deductible_amount}</span>
                                  </div>
                                )}
                                {verification.coverage_percentage && (
                                  <div>
                                    <span className="text-gray-600">Coverage:</span>
                                    <span className="ml-2 font-medium">{verification.coverage_percentage}%</span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-gray-600">Verified:</span>
                                  <span className="ml-2 font-medium">{new Date(verification.verified_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Claims Tab */}
            <TabsContent value="claims">
              <div className="space-y-4">
                {/* Claims Stats */}
                {claimsStats && (
                  <div className="grid grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-gray-600">Total Claims</div>
                        <div className="text-2xl font-bold">{claimsStats.total_claims}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-gray-600">Total Billed</div>
                        <div className="text-2xl font-bold">${claimsStats.total_billed?.toFixed(2)}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-gray-600">Total Paid</div>
                        <div className="text-2xl font-bold text-green-600">${claimsStats.total_paid?.toFixed(2)}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-sm text-gray-600">Denial Rate</div>
                        <div className="text-2xl font-bold text-red-600">{claimsStats.denial_rate}%</div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Insurance Claims</h3>
                  <Button onClick={() => setShowClaimModal(true)} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Submit New Claim
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading claims...</div>
                ) : claims.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No insurance claims yet. Click "Submit New Claim" to get started.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {claims.map((claim) => (
                      <Card key={claim.id} className="hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => {
                          setSelectedClaim(claim);
                          setShowClaimDetailModal(true);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold">{claim.client_name || getClientName(claim.client_id)}</h4>
                                {getStatusBadge(claim.status)}
                              </div>
                              <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
                                <div>
                                  <span className="text-gray-600">Claim #:</span>
                                  <span className="ml-2 font-medium">{claim.claim_number}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Service Date:</span>
                                  <span className="ml-2 font-medium">{claim.service_date}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Billed:</span>
                                  <span className="ml-2 font-medium">${claim.billed_amount?.toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Provider:</span>
                                  <span className="ml-2 font-medium">{claim.insurance_provider}</span>
                                </div>
                                {claim.paid_amount && (
                                  <div>
                                    <span className="text-gray-600">Paid:</span>
                                    <span className="ml-2 font-medium text-green-600">${claim.paid_amount.toFixed(2)}</span>
                                  </div>
                                )}
                                {claim.denial_reason && (
                                  <div className="col-span-2">
                                    <span className="text-gray-600">Denial Reason:</span>
                                    <span className="ml-2 font-medium text-red-600">{claim.denial_reason}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {claim.status === 'draft' && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubmitClaim(claim.id);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Submit
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={(e) => e.stopPropagation()}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Denials Tab */}
            <TabsContent value="denials">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Denied Claims</h3>
                {claims.filter(c => c.status === 'denied').length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No denied claims to review.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {claims.filter(c => c.status === 'denied').map((claim) => (
                      <Card key={claim.id}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold">{claim.client_name || getClientName(claim.client_id)}</h4>
                                {getStatusBadge(claim.status)}
                              </div>
                              <div className="space-y-2 text-sm">
                                <div>
                                  <span className="text-gray-600">Claim #:</span>
                                  <span className="ml-2 font-medium">{claim.claim_number}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Billed Amount:</span>
                                  <span className="ml-2 font-medium">${claim.billed_amount?.toFixed(2)}</span>
                                </div>
                                <div className="bg-red-50 p-3 rounded-md border border-red-200">
                                  <div className="font-semibold text-red-800 mb-1">Denial Reason:</div>
                                  <div className="text-red-700">{claim.denial_reason || 'No reason provided'}</div>
                                  {claim.denial_code && (
                                    <div className="text-sm text-red-600 mt-1">Code: {claim.denial_code}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleUpdateClaimStatus(claim.id, 'appealed')}
                                className="bg-purple-600 hover:bg-purple-700"
                              >
                                File Appeal
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setShowClaimDetailModal(true);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View Details
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Create Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Verify Insurance</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowVerificationModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="client_id">Client *</Label>
                  <select
                    id="client_id"
                    className="w-full p-2 border rounded-md"
                    value={verificationForm.client_id}
                    onChange={(e) => setVerificationForm({...verificationForm, client_id: e.target.value})}
                  >
                    <option value="">Select Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.first_name} {client.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="insurance_provider">Insurance Provider *</Label>
                    <Input
                      id="insurance_provider"
                      value={verificationForm.insurance_provider}
                      onChange={(e) => setVerificationForm({...verificationForm, insurance_provider: e.target.value})}
                      placeholder="e.g., Blue Cross, Medicaid"
                    />
                  </div>
                  <div>
                    <Label htmlFor="policy_number">Policy Number *</Label>
                    <Input
                      id="policy_number"
                      value={verificationForm.policy_number}
                      onChange={(e) => setVerificationForm({...verificationForm, policy_number: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="group_number">Group Number</Label>
                    <Input
                      id="group_number"
                      value={verificationForm.group_number}
                      onChange={(e) => setVerificationForm({...verificationForm, group_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="subscriber_relationship">Subscriber Relationship</Label>
                    <select
                      id="subscriber_relationship"
                      className="w-full p-2 border rounded-md"
                      value={verificationForm.subscriber_relationship}
                      onChange={(e) => setVerificationForm({...verificationForm, subscriber_relationship: e.target.value})}
                    >
                      <option value="self">Self</option>
                      <option value="parent">Parent</option>
                      <option value="spouse">Spouse</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="copay_amount">Copay Amount</Label>
                    <Input
                      id="copay_amount"
                      type="number"
                      step="0.01"
                      value={verificationForm.copay_amount}
                      onChange={(e) => setVerificationForm({...verificationForm, copay_amount: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="deductible_amount">Deductible</Label>
                    <Input
                      id="deductible_amount"
                      type="number"
                      step="0.01"
                      value={verificationForm.deductible_amount}
                      onChange={(e) => setVerificationForm({...verificationForm, deductible_amount: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="coverage_percentage">Coverage %</Label>
                    <Input
                      id="coverage_percentage"
                      type="number"
                      value={verificationForm.coverage_percentage}
                      onChange={(e) => setVerificationForm({...verificationForm, coverage_percentage: e.target.value})}
                      placeholder="80"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="verification_notes">Verification Notes</Label>
                  <Textarea
                    id="verification_notes"
                    value={verificationForm.verification_notes}
                    onChange={(e) => setVerificationForm({...verificationForm, verification_notes: e.target.value})}
                    rows={3}
                    placeholder="Additional notes about verification..."
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowVerificationModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateVerification} className="bg-blue-600 hover:bg-blue-700">
                    Create Verification
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Claim Modal */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Submit Insurance Claim</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowClaimModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="claim_client_id">Client *</Label>
                  <select
                    id="claim_client_id"
                    className="w-full p-2 border rounded-md"
                    value={claimForm.client_id}
                    onChange={(e) => setClaimForm({...claimForm, client_id: e.target.value})}
                  >
                    <option value="">Select Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.first_name} {client.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="service_date">Service Date *</Label>
                    <Input
                      id="service_date"
                      type="date"
                      value={claimForm.service_date}
                      onChange={(e) => setClaimForm({...claimForm, service_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="billed_amount">Billed Amount *</Label>
                    <Input
                      id="billed_amount"
                      type="number"
                      step="0.01"
                      value={claimForm.billed_amount}
                      onChange={(e) => setClaimForm({...claimForm, billed_amount: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="claim_insurance_provider">Insurance Provider *</Label>
                    <Input
                      id="claim_insurance_provider"
                      value={claimForm.insurance_provider}
                      onChange={(e) => setClaimForm({...claimForm, insurance_provider: e.target.value})}
                      placeholder="e.g., Blue Cross, Medicaid"
                    />
                  </div>
                  <div>
                    <Label htmlFor="claim_policy_number">Policy Number *</Label>
                    <Input
                      id="claim_policy_number"
                      value={claimForm.policy_number}
                      onChange={(e) => setClaimForm({...claimForm, policy_number: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="provider_name">Provider Name</Label>
                    <Input
                      id="provider_name"
                      value={claimForm.provider_name}
                      onChange={(e) => setClaimForm({...claimForm, provider_name: e.target.value})}
                      placeholder="Therapist/Provider name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="provider_npi">Provider NPI</Label>
                    <Input
                      id="provider_npi"
                      value={claimForm.provider_npi}
                      onChange={(e) => setClaimForm({...claimForm, provider_npi: e.target.value})}
                      placeholder="National Provider Identifier"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* CPT Codes Multi-Select */}
                  <div className="relative dropdown-container">
                    <Label htmlFor="cpt_codes">CPT Codes *</Label>
                    <div className="border rounded-md p-2 min-h-[42px] bg-white cursor-pointer" onClick={() => setShowCptDropdown(!showCptDropdown)}>
                      {claimForm.cpt_codes.length === 0 ? (
                        <span className="text-gray-400 text-sm">Select CPT codes...</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {claimForm.cpt_codes.map(code => (
                            <Badge key={code} className="bg-blue-100 text-blue-800 text-xs">
                              {code}
                              <X 
                                className="w-3 h-3 ml-1 cursor-pointer" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCptCode(code);
                                }}
                              />
                            </Badge>
                          ))}
                        </div>
                      )}
                      <ChevronDown className="absolute right-2 top-9 w-4 h-4 text-gray-500" />
                    </div>
                    
                    {showCptDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto">
                        {CPT_CODES.map(item => (
                          <div
                            key={item.code}
                            className={`p-2 hover:bg-gray-100 cursor-pointer flex items-start gap-2 ${
                              claimForm.cpt_codes.includes(item.code) ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => toggleCptCode(item.code)}
                          >
                            <input
                              type="checkbox"
                              checked={claimForm.cpt_codes.includes(item.code)}
                              onChange={() => {}}
                              className="mt-1"
                            />
                            <div>
                              <div className="font-semibold text-sm">{item.code}</div>
                              <div className="text-xs text-gray-600">{item.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Select one or more procedure codes</p>
                  </div>
                  
                  {/* ICD-10 Diagnosis Codes Multi-Select */}
                  <div className="relative dropdown-container">
                    <Label htmlFor="diagnosis_codes">ICD-10 Diagnosis Codes *</Label>
                    <div className="border rounded-md p-2 min-h-[42px] bg-white cursor-pointer" onClick={() => setShowIcdDropdown(!showIcdDropdown)}>
                      {claimForm.diagnosis_codes.length === 0 ? (
                        <span className="text-gray-400 text-sm">Select diagnosis codes...</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {claimForm.diagnosis_codes.map(code => (
                            <Badge key={code} className="bg-green-100 text-green-800 text-xs">
                              {code}
                              <X 
                                className="w-3 h-3 ml-1 cursor-pointer" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDiagnosisCode(code);
                                }}
                              />
                            </Badge>
                          ))}
                        </div>
                      )}
                      <ChevronDown className="absolute right-2 top-9 w-4 h-4 text-gray-500" />
                    </div>
                    
                    {showIcdDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto">
                        {ICD10_CODES.map(item => (
                          <div
                            key={item.code}
                            className={`p-2 hover:bg-gray-100 cursor-pointer flex items-start gap-2 ${
                              claimForm.diagnosis_codes.includes(item.code) ? 'bg-green-50' : ''
                            }`}
                            onClick={() => toggleDiagnosisCode(item.code)}
                          >
                            <input
                              type="checkbox"
                              checked={claimForm.diagnosis_codes.includes(item.code)}
                              onChange={() => {}}
                              className="mt-1"
                            />
                            <div>
                              <div className="font-semibold text-sm">{item.code}</div>
                              <div className="text-xs text-gray-600">{item.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Select one or more diagnosis codes</p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="service_description">Service Description *</Label>
                  <Textarea
                    id="service_description"
                    value={claimForm.service_description}
                    onChange={(e) => setClaimForm({...claimForm, service_description: e.target.value})}
                    rows={2}
                    placeholder="Brief description of service provided..."
                  />
                </div>

                <div>
                  <Label htmlFor="claim_notes">Claim Notes</Label>
                  <Textarea
                    id="claim_notes"
                    value={claimForm.claim_notes}
                    onChange={(e) => setClaimForm({...claimForm, claim_notes: e.target.value})}
                    rows={2}
                    placeholder="Internal notes about this claim..."
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowClaimModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateClaim} className="bg-green-600 hover:bg-green-700">
                    Create Claim
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Claim Detail Modal */}
      {showClaimDetailModal && selectedClaim && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-bold">Claim Details</h2>
                  <p className="text-sm text-gray-600">{selectedClaim.claim_number}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowClaimDetailModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Status:</span>
                  {getStatusBadge(selectedClaim.status)}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Client</div>
                    <div className="font-semibold">{selectedClaim.client_name || getClientName(selectedClaim.client_id)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Service Date</div>
                    <div className="font-semibold">{selectedClaim.service_date}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Insurance Provider</div>
                    <div className="font-semibold">{selectedClaim.insurance_provider}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Policy Number</div>
                    <div className="font-semibold">{selectedClaim.policy_number}</div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Service Information</h3>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-600">Description:</span>
                      <p className="mt-1">{selectedClaim.service_description}</p>
                    </div>
                    {selectedClaim.cpt_codes && selectedClaim.cpt_codes.length > 0 && (
                      <div>
                        <span className="text-sm text-gray-600">CPT Codes:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {selectedClaim.cpt_codes.map(code => (
                            <Badge key={code} variant="outline">{code}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedClaim.diagnosis_codes && selectedClaim.diagnosis_codes.length > 0 && (
                      <div>
                        <span className="text-sm text-gray-600">Diagnosis Codes:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {selectedClaim.diagnosis_codes.map(code => (
                            <Badge key={code} variant="outline">{code}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Financial Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Billed Amount</div>
                      <div className="text-lg font-bold">${selectedClaim.billed_amount?.toFixed(2)}</div>
                    </div>
                    {selectedClaim.approved_amount && (
                      <div>
                        <div className="text-sm text-gray-600">Approved Amount</div>
                        <div className="text-lg font-bold text-blue-600">${selectedClaim.approved_amount.toFixed(2)}</div>
                      </div>
                    )}
                    {selectedClaim.paid_amount && (
                      <div>
                        <div className="text-sm text-gray-600">Paid Amount</div>
                        <div className="text-lg font-bold text-green-600">${selectedClaim.paid_amount.toFixed(2)}</div>
                      </div>
                    )}
                    {selectedClaim.patient_responsibility && (
                      <div>
                        <div className="text-sm text-gray-600">Client Responsibility</div>
                        <div className="text-lg font-bold">${selectedClaim.patient_responsibility.toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedClaim.denial_reason && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-2 text-red-800">Denial Information</h3>
                    <div className="bg-red-50 p-4 rounded-md border border-red-200">
                      <div className="text-sm text-gray-600 mb-1">Reason:</div>
                      <div className="font-medium text-red-800">{selectedClaim.denial_reason}</div>
                      {selectedClaim.denial_code && (
                        <div className="mt-2 text-sm">
                          <span className="text-gray-600">Code:</span>
                          <span className="ml-2 font-medium">{selectedClaim.denial_code}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-4 border-t">
                  {selectedClaim.status === 'submitted' && (
                    <>
                      <Button
                        onClick={() => handleUpdateClaimStatus(selectedClaim.id, 'approved')}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Mark Approved
                      </Button>
                      <Button
                        onClick={() => handleUpdateClaimStatus(selectedClaim.id, 'denied')}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Mark Denied
                      </Button>
                    </>
                  )}
                  {selectedClaim.status === 'denied' && !selectedClaim.appeal_filed && (
                    <Button
                      onClick={() => handleUpdateClaimStatus(selectedClaim.id, 'appealed')}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      File Appeal
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setShowClaimDetailModal(false)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsuranceClaimsManagement;
